import * as React from "react";
import { PencilSimple, FloppyDisk, X, FileText } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CustomerDetailLabels } from "../types";

interface Props {
  customerId: string;
  initialNotes: string;
  labels: CustomerDetailLabels;
  onSave?: (customerId: string, notes: string) => Promise<void> | void;
  isSaving?: boolean;
}

export function NotesTab({ customerId, initialNotes, labels, onSave, isSaving }: Props) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(initialNotes);
  const [committed, setCommitted] = React.useState(initialNotes);

  React.useEffect(() => {
    setDraft(initialNotes);
    setCommitted(initialNotes);
  }, [initialNotes, customerId]);

  const handleSave = async () => {
    await onSave?.(customerId, draft);
    setCommitted(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(committed);
    setEditing(false);
  };

  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface shadow-biz-sm">
      <div className="px-5 py-4 border-b border-biz-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText size={16} weight="regular" className="text-biz-foreground-muted" />
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-biz-foreground-subtle">
            {labels.notes.title}
          </h2>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="h-11 px-3 rounded-biz-btn text-sm font-semibold text-biz-foreground-muted hover:bg-biz-muted inline-flex items-center gap-1.5 biz-focus-ring transition-colors"
          >
            <PencilSimple size={16} weight="regular" />
            {labels.notes.edit}
          </button>
        )}
      </div>

      <div className="p-5">
        {editing ? (
          <div className="space-y-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={labels.notes.placeholder}
              rows={10}
              className={cn(
                "w-full rounded-biz-btn border border-biz-border bg-biz-bg p-3 text-sm text-biz-foreground",
                "placeholder:text-biz-foreground-subtle resize-y biz-focus-ring",
              )}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="h-11 px-4 rounded-biz-btn text-sm font-semibold text-biz-foreground-muted hover:bg-biz-muted inline-flex items-center gap-1.5 biz-focus-ring transition-colors disabled:opacity-50"
              >
                <X size={16} weight="regular" />
                {labels.notes.cancel}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="h-11 px-4 rounded-biz-btn bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover text-sm font-semibold inline-flex items-center gap-1.5 biz-focus-ring transition-colors disabled:opacity-60"
              >
                <FloppyDisk size={16} weight="regular" />
                {isSaving ? labels.notes.saving : labels.notes.save}
              </button>
            </div>
          </div>
        ) : committed ? (
          <p className="text-sm text-biz-foreground leading-relaxed whitespace-pre-wrap">{committed}</p>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-biz-foreground-subtle italic">{labels.notes.empty}</p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-3 h-11 px-4 rounded-biz-btn border border-biz-border text-sm font-semibold text-biz-foreground-muted hover:bg-biz-muted inline-flex items-center gap-1.5 biz-focus-ring transition-colors"
            >
              <PencilSimple size={16} weight="regular" />
              {labels.notes.edit}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
